import type { CSSProperties, ReactNode } from "react";

interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  /** Extra classes, e.g. to position it within a shape built from several blocks. */
  className?: string;
}

/** One shimmering placeholder block. Compose a few into the shape of the content that's coming. */
export function Skeleton({ width = "100%", height = 14, radius = 6, className }: SkeletonProps) {
  const style: CSSProperties = { width, height, borderRadius: radius };
  return <span className={className ? `bw-skel ${className}` : "bw-skel"} style={style} />;
}

interface SkeletonGroupProps {
  /** What a screen reader announces instead of the placeholder shapes. */
  label: string;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a group of Skeleton blocks with the accessibility contract loading placeholders need: a
 * screen reader hears `label` (what used to be read, e.g. "Loading your conversation…") instead of
 * the placeholder shapes. `className` goes on the inner, visible layer, so it keeps whatever layout
 * (flex, grid, list) the real content would use.
 */
export function SkeletonGroup({ label, children, className }: SkeletonGroupProps) {
  return (
    <div role="status" aria-busy="true">
      <span className="bw-sr">{label}</span>
      <div className={className} aria-hidden="true">{children}</div>
    </div>
  );
}

/**
 * The shape of a page: a header line with a few nav pills, a hero (title, two lines, an image
 * block), then a few more text-line rows. Used for the very first load, whether that's the whole
 * canvas (no page has ever loaded) or the preview frame's first page.
 */
export function PageOutlineSkeleton({ label }: { label: string }) {
  return (
    <SkeletonGroup label={label} className="bw-page-skel">
      <div className="bw-page-skel-head">
        <Skeleton width={110} height={13} radius={4} />
        <div className="bw-page-skel-nav">
          <Skeleton width={54} height={10} radius={999} />
          <Skeleton width={64} height={10} radius={999} />
          <Skeleton width={46} height={10} radius={999} />
        </div>
      </div>
      <div className="bw-page-skel-body">
        <Skeleton width="55%" height={26} radius={6} />
        <Skeleton width="40%" height={13} radius={4} />
        <Skeleton width="32%" height={13} radius={4} />
        <Skeleton width="100%" height={170} radius={10} />
        <Skeleton width="90%" height={13} radius={4} />
        <Skeleton width="78%" height={13} radius={4} />
        <Skeleton width="60%" height={13} radius={4} />
      </div>
    </SkeletonGroup>
  );
}
